import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BlobPreloader } from '../components/BlobPreloader';
import { 
  Download, 
  X, 
  Eye, 
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Calendar,
  Clock,
  User,
  SlidersHorizontal,
  FileAudio,
  Trash2,
  CheckSquare,
  Square,
  Play,
  Pause,
  Volume2,
  Package,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  ThumbsUp,
  ThumbsDown,
  Users,
  List,
  Phone,
  Store,
  Filter,
  Brain,
  RefreshCw
} from 'lucide-react';
import { Link, useSearchParams, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { format, subDays, startOfWeek, startOfMonth } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sub-category options
const SUB_CATEGORIES = {
  interested: [
    "Product Fit",
    "Price Advantage",
    "Brand Trust",
    "Delivery/Shipping",
    "Offer/Promotion",
    "Availability",
    "Business Fit",
    "Payment Terms",
    "Competitive Advantage"
  ],
  not_interested: [
    "Product Mismatch",
    "Price Too High",
    "Competitor Preference",
    "Delivery Issues",
    "No Business Need",
    "Brand Trust Issues",
    "Payment Terms",
    "Margin/ROI Concerns",
    "Stock/Availability"
  ]
};

// Mini Audio Player for table rows with speed control, click-to-seek, and skip buttons
const MiniAudioPlayer = ({ audioUrl }) => {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const speeds = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  const togglePlay = async (e) => {
    e.stopPropagation();
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        try {
          await audioRef.current.play();
          setIsPlaying(true);
          setHasError(false);
        } catch (err) {
          console.error('Audio play error:', err);
          setHasError(true);
        }
        setIsLoading(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current && audioRef.current.duration) {
      const prog = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(prog);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    if (audioRef.current && progressRef.current && duration) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const pct = clickX / rect.width;
      audioRef.current.currentTime = pct * duration;
    }
  };

  const skipTime = (seconds, e) => {
    e.stopPropagation();
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
    }
  };

  const changeSpeed = (speed) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
    setShowSpeedMenu(false);
  };

  if (!audioUrl) {
    return (
      <div className="flex items-center gap-2 text-slate-400">
        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
          <Volume2 className="w-4 h-4" />
        </div>
        <span className="text-xs">No audio</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onError={() => setHasError(true)}
      />
      
      {/* Skip Back -15s */}
      <button
        onClick={(e) => skipTime(-15, e)}
        className="px-1.5 py-0.5 text-[9px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-full transition-colors"
        title="Skip back 15s"
      >
        −15
      </button>
      
      {/* Play/Pause Button */}
      <button
        onClick={togglePlay}
        disabled={hasError}
        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
          hasError 
            ? 'bg-rose-100 text-rose-500 cursor-not-allowed' 
            : 'bg-[#2eb4ac] hover:bg-[#259790] text-white'
        }`}
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : hasError ? (
          <Volume2 className="w-4 h-4" />
        ) : isPlaying ? (
          <Pause className="w-4 h-4" />
        ) : (
          <Play className="w-4 h-4 ml-0.5" />
        )}
      </button>
      
      {/* Skip Forward +15s */}
      <button
        onClick={(e) => skipTime(15, e)}
        className="px-1.5 py-0.5 text-[9px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-full transition-colors"
        title="Skip forward 15s"
      >
        +15
      </button>
      
      {/* Clickable Progress Bar */}
      <div 
        ref={progressRef}
        onClick={handleSeek}
        className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden cursor-pointer hover:h-2 transition-all"
      >
        <div 
          className="h-full bg-[#2eb4ac] transition-all pointer-events-none" 
          style={{ width: `${progress}%` }} 
        />
      </div>
      
      {/* Speed Control */}
      <div className="relative">
        <button
          onClick={(e) => { e.stopPropagation(); setShowSpeedMenu(!showSpeedMenu); }}
          className="px-1.5 py-0.5 text-[10px] font-medium bg-slate-100 hover:bg-slate-200 text-slate-600 rounded transition-colors"
        >
          {playbackSpeed}x
        </button>
        {showSpeedMenu && (
          <div className="absolute bottom-full left-0 mb-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50 min-w-[60px]">
            {speeds.map(speed => (
              <button
                key={speed}
                onClick={(e) => { e.stopPropagation(); changeSpeed(speed); }}
                className={`w-full px-2 py-1 text-xs text-left hover:bg-slate-50 ${
                  playbackSpeed === speed ? 'text-[#2eb4ac] font-medium bg-[#2eb4ac]/5' : 'text-slate-700'
                }`}
              >
                {speed}x
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Product Dropdown Component
const ProductDropdown = ({ callId, currentProducts, products, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(new Set(currentProducts || []));

  const toggleProduct = async (product, brand) => {
    const productLabel = `${brand}: ${product}`;
    const newSelected = new Set(selected);
    
    if (newSelected.has(productLabel)) {
      newSelected.delete(productLabel);
    } else {
      newSelected.add(productLabel);
    }
    
    setSelected(newSelected);
    
    try {
      await axios.patch(`${API}/calls/${callId}/products`, Array.from(newSelected));
      onUpdate();
    } catch (error) {
      toast.error('Failed to update products');
    }
  };

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className="flex items-center gap-1 px-2 py-1.5 bg-slate-50 hover:bg-slate-100 rounded-lg text-xs transition-colors max-w-[160px]"
      >
        <Package className="w-3 h-3 text-slate-400 flex-shrink-0" />
        <span className="truncate text-slate-700">
          {selected.size > 0 ? `${selected.size} product(s)` : 'Select Products'}
        </span>
        <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0" />
      </button>
      
      {isOpen && (
        <div className="absolute left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50 max-h-64 overflow-y-auto">
          {Object.entries(products).map(([brand, items]) => (
            <div key={brand}>
              <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider bg-slate-50">
                {brand}
              </div>
              {items.map(product => {
                const productLabel = `${brand}: ${product}`;
                const isSelected = selected.has(productLabel);
                return (
                  <button
                    key={productLabel}
                    onClick={(e) => { e.stopPropagation(); toggleProduct(product, brand); }}
                    className={`w-full px-3 py-2 text-left text-xs flex items-center gap-2 hover:bg-slate-50 ${
                      isSelected ? 'text-[#2eb4ac] bg-[#2eb4ac]/5' : 'text-slate-700'
                    }`}
                  >
                    {isSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5 text-slate-300" />}
                    {product}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Sub-Category Quick Selector Component
const SubCategorySelector = ({ callId, currentVerdict, currentSubCategory, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Determine which sub-categories to show based on verdict
  const verdictType = currentVerdict === 'Interested' ? 'interested' : 'not_interested';
  const subCategories = SUB_CATEGORIES[verdictType];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCategory = async (category) => {
    try {
      await axios.patch(`${API}/calls/${callId}/sub-category`, {
        sub_category: category,
        verdict_type: verdictType
      });
      toast.success('Category updated');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update category');
    }
    setIsOpen(false);
  };

  const isInterested = currentVerdict === 'Interested';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border ${
          isInterested 
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100' 
            : 'bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100'
        }`}
        data-testid={`sub-category-btn-${callId}`}
      >
        <Tag className="w-3 h-3" />
        <span className="max-w-[100px] truncate">
          {currentSubCategory || 'Classify'}
        </span>
        <ChevronDown className="w-3 h-3 flex-shrink-0" />
      </button>
      
      {isOpen && (
        <div className="absolute left-0 mt-1 w-52 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50 max-h-72 overflow-y-auto">
          <div className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
            isInterested ? 'text-emerald-600 bg-emerald-50' : 'text-rose-600 bg-rose-50'
          }`}>
            {isInterested ? (
              <div className="flex items-center gap-1.5">
                <ThumbsUp className="w-3 h-3" />
                Why Interested?
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <ThumbsDown className="w-3 h-3" />
                Why Not Interested?
              </div>
            )}
          </div>
          {subCategories.map(category => (
            <button
              key={category}
              onClick={(e) => { e.stopPropagation(); handleSelectCategory(category); }}
              className={`w-full px-3 py-2 text-left text-xs flex items-center gap-2 hover:bg-slate-50 transition-colors ${
                currentSubCategory === category 
                  ? (isInterested ? 'text-emerald-600 bg-emerald-50 font-medium' : 'text-rose-600 bg-rose-50 font-medium')
                  : 'text-slate-700'
              }`}
            >
              {currentSubCategory === category && <CheckSquare className="w-3.5 h-3.5 flex-shrink-0" />}
              {currentSubCategory !== category && <div className="w-3.5 h-3.5 flex-shrink-0" />}
              {category}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


// ============ Confirmation Modal ============
const ConfirmModal = ({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', tone = 'danger', requireTypedConfirm, onConfirm, onCancel }) => {
  const [typedValue, setTypedValue] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  const canConfirm = !requireTypedConfirm || typedValue === requireTypedConfirm;
  const toneClasses = tone === 'danger'
    ? 'bg-rose-600 hover:bg-rose-700'
    : 'bg-[#2eb4ac] hover:bg-[#239e98]';

  const handleConfirm = async () => {
    if (!canConfirm || isWorking) return;
    setIsWorking(true);
    try { await onConfirm(); } finally { setIsWorking(false); }
  };

  // Close on escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !isWorking) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isWorking, onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in"
      onClick={(e) => { if (e.target === e.currentTarget && !isWorking) onCancel(); }}
      data-testid="confirm-modal"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${tone === 'danger' ? 'bg-rose-100 text-rose-600' : 'bg-[#2eb4ac]/10 text-[#2eb4ac]'}`}>
              <Trash2 className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-1.5" data-testid="confirm-title">{title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{message}</p>

              {requireTypedConfirm && (
                <div className="mt-4">
                  <label className="block text-xs text-slate-500 mb-1.5">
                    Type <span className="font-mono font-semibold text-slate-700">{requireTypedConfirm}</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={typedValue}
                    onChange={(e) => setTypedValue(e.target.value)}
                    autoFocus
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-400"
                    data-testid="confirm-typed-input"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-slate-50 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={isWorking}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
            data-testid="confirm-cancel-btn"
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || isWorking}
            className={`px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${toneClasses}`}
            data-testid="confirm-action-btn"
          >
            {isWorking ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============ Bulk Analyze Progress Modal — sci-fi / futuristic ============
const AnalyzeProgressModal = ({ progress }) => {
  const { total, done, analyzed, skipped, failed } = progress;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const isComplete = done >= total;

  // Stable random particles
  const particles = React.useMemo(
    () => Array.from({ length: 28 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 4,
      duration: 3 + Math.random() * 3,
      size: 1 + Math.random() * 2,
    })),
    []
  );

  // Waveform bars
  const bars = React.useMemo(
    () => Array.from({ length: 40 }).map((_, i) => ({
      id: i,
      height: 16 + Math.sin(i * 0.55) * 28 + Math.random() * 18,
      delay: (i * 0.04) % 1.2,
    })),
    []
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4"
      data-testid="analyze-progress-modal"
    >
      <div className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-[#05080f] border border-[#2eb4ac]/30 shadow-[0_0_60px_-10px_rgba(46,180,172,0.6)]">

        {/* Animated gradient mesh background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#2eb4ac] opacity-20 blur-3xl animate-mesh-a" />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#5fd6cf] opacity-15 blur-3xl animate-mesh-b" />
          <div className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full bg-purple-600 opacity-10 blur-3xl animate-mesh-c" />
        </div>

        {/* Grid lines */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        {/* Floating particles */}
        <div className="absolute inset-0 pointer-events-none">
          {particles.map(p => (
            <span
              key={p.id}
              className="absolute rounded-full bg-[#5fd6cf]"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                opacity: 0.6,
                animation: `particle-float ${p.duration}s ease-in-out ${p.delay}s infinite`,
              }}
            />
          ))}
        </div>

        <div className="relative px-7 py-8">
          {/* Top label strip */}
          <div className="flex items-center justify-between mb-7">
            <div className="flex items-center gap-2">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#2eb4ac] opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2eb4ac]" />
              </span>
              <p className="text-[10px] tracking-[0.3em] font-mono uppercase text-[#5fd6cf]">
                {isComplete ? 'Pipeline · Idle' : 'Pipeline · Active'}
              </p>
            </div>
            <p className="text-[10px] tracking-[0.2em] font-mono uppercase text-slate-400">
              v1 · helix.core
            </p>
          </div>

          {/* Central AI core animation */}
          <div className="flex items-center justify-center mb-7">
            <div className="relative w-44 h-44">
              {/* Outer rotating ring */}
              <div className="absolute inset-0 rounded-full border border-[#2eb4ac]/40" style={{ animation: 'spin-slow 12s linear infinite' }}>
                <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#2eb4ac] shadow-[0_0_10px_2px_rgba(46,180,172,0.8)]" />
              </div>
              {/* Middle ring */}
              <div className="absolute inset-4 rounded-full border border-[#5fd6cf]/30" style={{ animation: 'spin-rev 8s linear infinite' }}>
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#5fd6cf]" />
              </div>
              {/* Inner ring */}
              <div className="absolute inset-8 rounded-full border-2 border-dashed border-[#2eb4ac]/50" style={{ animation: 'spin-slow 6s linear infinite' }} />

              {/* Center percentage */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-[10px] tracking-[0.3em] font-mono uppercase text-[#5fd6cf] mb-1">
                  {isComplete ? 'COMPLETE' : 'PROCESSING'}
                </p>
                <p className="text-4xl font-bold text-white tracking-tight tabular-nums">
                  {pct}<span className="text-xl text-slate-500">%</span>
                </p>
                <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                  {done} / {total}
                </p>
              </div>
            </div>
          </div>

          {/* Waveform — visualises the audio being processed */}
          <div className="flex items-end justify-center gap-[3px] h-12 mb-6">
            {bars.map((b) => (
              <span
                key={b.id}
                className="w-[3px] rounded-full bg-gradient-to-t from-[#2eb4ac] to-[#5fd6cf]"
                style={{
                  height: `${b.height}%`,
                  animation: isComplete
                    ? 'none'
                    : `wave-pulse-2 1.4s ease-in-out ${b.delay}s infinite`,
                  opacity: isComplete ? 0.4 : 1,
                }}
              />
            ))}
          </div>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-[10px] font-mono tracking-wider uppercase mb-2">
              <span className="text-slate-400">Throughput</span>
              <span className="text-[#5fd6cf] tabular-nums" data-testid="progress-percent">
                {done}/{total} · {pct}%
              </span>
            </div>
            <div className="relative w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#2eb4ac] via-[#5fd6cf] to-[#2eb4ac] transition-all duration-500"
                style={{
                  width: `${pct}%`,
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 2s linear infinite',
                  boxShadow: '0 0 12px rgba(95,214,207,0.7)',
                }}
              />
            </div>
          </div>

          {/* Stats — terminal feel */}
          <div className="grid grid-cols-3 gap-2 mb-2">
            <StatPill label="Analyzed" value={analyzed} color="emerald" testid="analyzed-count" />
            <StatPill label="Skipped" value={skipped} color="slate" />
            <StatPill label="Failed" value={failed} color="rose" />
          </div>

          {/* Status text — typewriter-style */}
          <p className="text-center text-[11px] tracking-wider font-mono uppercase text-slate-500 mt-5">
            {isComplete
              ? '> analysis complete · ready'
              : '> transcribing → tokenizing → extracting intent · 10–30s per call'}
            <span className="inline-block w-1.5 h-3 bg-[#2eb4ac] ml-1 align-middle animate-pulse" />
          </p>
        </div>

        {/* Bottom data stream effect */}
        <div className="relative h-8 bg-[#030610] border-t border-[#2eb4ac]/20 overflow-hidden">
          <div className="absolute inset-0 flex items-center" style={{ animation: 'data-stream 8s linear infinite' }}>
            <p className="text-[9px] font-mono text-[#2eb4ac]/40 tracking-widest whitespace-nowrap px-2">
              {'0x4a3f.b2c1 :: GROQ_LLAMA-3.3-70B :: WHISPER-1 :: GRIDFS :: 0xff31.8a92 :: ANALYZER.RUN(batch=3) :: TOKENS_OK :: SIGNALS_EXTRACTED :: 0xb1e2.4d77 :: GROQ_LLAMA-3.3-70B :: WHISPER-1 :: 0x9c1f.3e88'.repeat(2)}
            </p>
          </div>
        </div>

        {/* Inline keyframes */}
        <style>{`
          @keyframes spin-slow { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
          @keyframes spin-rev  { from { transform: rotate(0deg) } to { transform: rotate(-360deg) } }
          @keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }
          @keyframes wave-pulse-2 {
            0%,100% { transform: scaleY(0.35); opacity: 0.55; }
            50%     { transform: scaleY(1);    opacity: 1; }
          }
          @keyframes particle-float {
            0%,100% { transform: translate(0,0); opacity: 0.6; }
            50%     { transform: translate(8px, -12px); opacity: 1; }
          }
          @keyframes data-stream {
            from { transform: translateX(0); }
            to   { transform: translateX(-50%); }
          }
          @keyframes mesh-a { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(30px,20px) scale(1.1); } }
          @keyframes mesh-b { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-25px,-15px) scale(1.05); } }
          @keyframes mesh-c { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(15px,-25px) scale(1.08); } }
          .animate-mesh-a { animation: mesh-a 14s ease-in-out infinite; }
          .animate-mesh-b { animation: mesh-b 18s ease-in-out infinite; }
          .animate-mesh-c { animation: mesh-c 16s ease-in-out infinite; }
        `}</style>
      </div>
    </div>
  );
};

const StatPill = ({ label, value, color, testid }) => {
  const palette = {
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'text-emerald-500/70', border: 'border-emerald-500/20' },
    rose:    { bg: 'bg-rose-500/10',    text: 'text-rose-400',    label: 'text-rose-500/70',    border: 'border-rose-500/20' },
    slate:   { bg: 'bg-slate-500/10',   text: 'text-slate-300',   label: 'text-slate-500',      border: 'border-slate-600/30' },
  }[color || 'slate'];
  return (
    <div className={`rounded-lg border ${palette.border} ${palette.bg} px-3 py-2.5 text-center backdrop-blur-sm`}>
      <p className={`text-xl font-bold tabular-nums ${palette.text}`} data-testid={testid}>{value}</p>
      <p className={`text-[10px] font-mono uppercase tracking-[0.15em] mt-0.5 ${palette.label}`}>{label}</p>
    </div>
  );
};


// Inline mini-form rendered on name-only seller rows. Lets the user attach
// a real phone number to all unphoned calls for this customer with one click.
const PhoneAttachInline = ({ name, synthKey, onAttached }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const digits = (value || '').replace(/\D/g, '');
    if (digits.length < 6) {
      toast.error('Enter at least 6 digits');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/calls/attach-phone`, { name, phone: digits });
      const updated = res.data?.updated || 0;
      if (updated > 0) {
        toast.success(`Linked ${updated} call${updated > 1 ? 's' : ''} to +${digits}`);
        onAttached?.();
      } else {
        toast.error('No calls matched');
      }
      setOpen(false);
      setValue('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to attach phone');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-[#2eb4ac] hover:text-[#1e8580] hover:underline transition-colors"
        data-testid={`attach-phone-btn-${synthKey}`}
        title="Attach a phone number — all unphoned calls for this customer will be merged into the right seller row"
      >
        <span className="italic text-slate-400 font-normal">No phone number on file</span>
        <span className="text-slate-300">·</span>
        <span>+ Add phone</span>
      </button>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1.5"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="tel"
        autoFocus
        placeholder="Enter phone…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit(e);
          if (e.key === 'Escape') { setOpen(false); setValue(''); }
        }}
        disabled={busy}
        className="px-2 py-0.5 text-xs border border-slate-300 rounded-md w-32 focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/30 focus:border-[#2eb4ac] disabled:opacity-50"
        data-testid={`attach-phone-input-${synthKey}`}
      />
      <button
        type="button"
        onClick={submit}
        disabled={busy}
        className="px-2 py-0.5 bg-[#2eb4ac] text-white text-xs font-medium rounded-md hover:bg-[#268f87] disabled:opacity-50 transition-colors"
        data-testid={`attach-phone-save-${synthKey}`}
      >
        {busy ? '...' : 'Save'}
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); setValue(''); }}
        disabled={busy}
        className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
        title="Cancel"
      >
        ✕
      </button>
    </span>
  );
};




// ===== QC components — used inside the inline call card =====

// Maps each QC category to a Tailwind pill palette so reps can scan rows quickly.
const QC_PALETTE = {
  'Asked to Share Catalogue': { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-100' },
  'Requested Order List':     { bg: 'bg-green-50',   text: 'text-green-700',   ring: 'ring-green-100' },
  'Instock':                  { bg: 'bg-teal-50',    text: 'text-teal-700',    ring: 'ring-teal-100' },
  'Payment Process':          { bg: 'bg-cyan-50',    text: 'text-cyan-700',    ring: 'ring-cyan-100' },
  'Enquiry Call':             { bg: 'bg-blue-50',    text: 'text-blue-700',    ring: 'ring-blue-100' },
  'Offline Website Customer': { bg: 'bg-indigo-50',  text: 'text-indigo-700',  ring: 'ring-indigo-100' },
  'Call Back Later':          { bg: 'bg-yellow-50',  text: 'text-yellow-700',  ring: 'ring-yellow-100' },
  'Price Comparison':         { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-100' },
  'Already Billing':          { bg: 'bg-orange-50',  text: 'text-orange-700',  ring: 'ring-orange-100' },
  'Wrong Submission':         { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-100' },
  'Wrong Number':             { bg: 'bg-pink-50',    text: 'text-pink-700',    ring: 'ring-pink-100' },
  'Hung Call':                { bg: 'bg-red-50',     text: 'text-red-700',     ring: 'ring-red-100' },
  'Incomplete Call':          { bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-200' },
  'NA':                       { bg: 'bg-slate-50',   text: 'text-slate-400',   ring: 'ring-slate-100' },
};

const QCCategoryPill = ({ call, categories, onUpdate }) => {
  const current = call.qc_category || '';
  const palette = QC_PALETTE[current] || { bg: 'bg-slate-50', text: 'text-slate-500', ring: 'ring-slate-100' };
  const save = async (newCat) => {
    try {
      await axios.patch(`${API}/calls/${call.id}/qc`, { qc_category: newCat });
      onUpdate?.(newCat);
      toast.success('Category updated');
    } catch {
      toast.error('Failed to update category');
    }
  };
  return (
    <select
      value={current}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => { e.stopPropagation(); save(e.target.value); }}
      className={`px-2.5 py-1 text-xs font-medium rounded-full ring-1 border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/40 ${palette.bg} ${palette.text} ${palette.ring}`}
      data-testid={`qc-category-${call.id}`}
      title={`Call Category${current ? ` — ${current}` : ''}`}
    >
      <option value="">Category —</option>
      {categories.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
  );
};

const QCRemarkInline = ({ call, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(call.qc_remark || '');
  useEffect(() => { setValue(call.qc_remark || ''); }, [call.qc_remark]);

  const save = async () => {
    const trimmed = (value || '').trim();
    if (trimmed === (call.qc_remark || '')) { setEditing(false); return; }
    try {
      await axios.patch(`${API}/calls/${call.id}/qc`, { qc_remark: trimmed });
      onUpdate?.(trimmed);
      toast.success('Remark updated');
      setEditing(false);
    } catch {
      toast.error('Failed to update remark');
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1.5 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Remark</span>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') { setValue(call.qc_remark || ''); setEditing(false); }
          }}
          onBlur={save}
          placeholder="terse note…"
          className="flex-1 min-w-0 px-2 py-0.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/30 focus:border-[#2eb4ac]"
          data-testid={`qc-remark-input-${call.id}`}
        />
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="inline-flex items-baseline gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors min-w-0 max-w-full"
      title="Click to edit remark"
      data-testid={`qc-remark-${call.id}`}
    >
      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex-shrink-0">Remark</span>
      <span className={`truncate ${call.qc_remark ? 'text-slate-700 italic' : 'text-slate-300'}`}>
        {call.qc_remark || 'Add remark…'}
      </span>
    </button>
  );
};

const AgentInline = ({ call, agents, onUpdate }) => {
  const save = async (newId) => {
    try {
      await axios.patch(`${API}/calls/${call.id}/qc`, { agent_id: newId || '' });
      onUpdate?.(newId || null);
      toast.success('Agent updated');
    } catch {
      toast.error('Failed to update agent');
    }
  };
  return (
    <span className="inline-flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Agent</span>
      <select
        value={call.agent_id || ''}
        onChange={(e) => { e.stopPropagation(); save(e.target.value); }}
        className="px-2 py-0.5 text-xs border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/30 focus:border-[#2eb4ac] cursor-pointer"
        data-testid={`agent-${call.id}`}
      >
        <option value="">Unassigned</option>
        {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
    </span>
  );
};


// ===== Bulk Edit Modal — updates qc_category / qc_remark / agent / source
// on many calls at once. Only fields the user actually touches are sent. =====
const BulkEditModal = ({ count, categories, agents, dataSources, onCancel, onSubmit }) => {
  const [qcCategory, setQcCategory] = useState('');
  const [qcRemark, setQcRemark] = useState('');
  const [agentId, setAgentId] = useState('');
  const [dataSource, setDataSource] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {};
    if (qcCategory) payload.qc_category = qcCategory;
    if (qcRemark) payload.qc_remark = qcRemark;
    if (agentId) payload.agent_id = agentId;
    if (dataSource) payload.data_source = dataSource;
    if (Object.keys(payload).length === 0) {
      toast.error('Pick at least one field to update');
      return;
    }
    setBusy(true);
    await onSubmit(payload);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onCancel} data-testid="bulk-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-900">Update {count} call{count !== 1 ? 's' : ''}</h3>
          <p className="text-sm text-slate-500 mt-1">Only the fields you fill will be updated. Empty fields are ignored.</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Category */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Call Category</label>
            <select
              value={qcCategory}
              onChange={(e) => setQcCategory(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/30 focus:border-[#2eb4ac] bg-white"
              data-testid="bulk-edit-category"
            >
              <option value="">— keep existing —</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Remark */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Remark</label>
            <input
              type="text"
              value={qcRemark}
              onChange={(e) => setQcRemark(e.target.value)}
              placeholder="— keep existing —"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/30 focus:border-[#2eb4ac]"
              data-testid="bulk-edit-remark"
            />
          </div>

          {/* Agent */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Agent</label>
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/30 focus:border-[#2eb4ac] bg-white"
              data-testid="bulk-edit-agent"
            >
              <option value="">— keep existing —</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          {/* Data Source */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Data Source</label>
            <select
              value={dataSource}
              onChange={(e) => setDataSource(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/30 focus:border-[#2eb4ac] bg-white"
              data-testid="bulk-edit-source"
            >
              <option value="">— keep existing —</option>
              {dataSources.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              data-testid="bulk-edit-submit"
            >
              {busy ? 'Updating…' : `Update ${count} call${count !== 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


const CallLogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();


  const navigate = useNavigate();
  
  const [calls, setCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [agents, setAgents] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCalls, setSelectedCalls] = useState(new Set());
  // Phones (seller keys) that own at least one currently-selected call.
  // Used to visually highlight selected rows without needing to expand them.
  const [selectedPhones, setSelectedPhones] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  
  // Grouped view state
  // Debounced search removed — search now fires on Enter only.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const submitSearch = () => {
    const trimmed = (searchQuery || '').trim();
    if (trimmed === debouncedSearch) return; // No-op if unchanged
    setDebouncedSearch(trimmed);
    setCurrentPage(1);
  };
  // Allow clearing the search to immediately reset results
  useEffect(() => {
    if ((searchQuery || '') === '' && debouncedSearch !== '') {
      setDebouncedSearch('');
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Locally filter grouped data by search query (phone / seller name / seller id / store name)
  const [groupedData, setGroupedData] = useState([]);

  // visibleGroups kept for backwards-compat; backend already filters server-side now,
  // so just return groupedData directly.
  const visibleGroups = groupedData;

  const [groupedTotal, setGroupedTotal] = useState(0);
  const [expandedSellers, setExpandedSellers] = useState(new Set());
  const [sellerCalls, setSellerCalls] = useState({}); // phone -> calls[]
  const [loadingSellerCalls, setLoadingSellerCalls] = useState(new Set());
  
  // Pagination state - initialize from URL params
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCalls, setTotalCalls] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  
  // Sync page changes to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (currentPage > 1) {
      params.set('page', currentPage.toString());
    } else {
      params.delete('page');
    }
    setSearchParams(params, { replace: true });
  }, [currentPage]);
  
  // Grouped sort state (for sellers table)
  const [groupSort, setGroupSort] = useState({ key: 'latest_call_date', order: 'desc' });
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'analyzed_at', direction: 'desc' });
  
  // Initialize filters from URL params - including new drill-down filters
  const [filters, setFilters] = useState({
    verdict: searchParams.get('verdict') || '',
    lifecycle: searchParams.get('stage') || '',
    agent: searchParams.get('agent') || '',
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || '',
    subCategory: searchParams.get('subCategory') || '',
    product: searchParams.get('product') || '',
    brand: searchParams.get('brand') || '',
    competitorBrand: searchParams.get('competitorBrand') || '',
    buyingStage: searchParams.get('buyingStage') || '',
    dataSource: searchParams.get('dataSource') || '',
    qcCategory: searchParams.get('qcCategory') || '',
    qcSubcategory: searchParams.get('qcSubcategory') || '',
    tier: searchParams.get('tier') || '',
  });

  const [dataSources, setDataSources] = useState([]);
  const [qcCategories, setQcCategories] = useState([]);
  const [qcSubcategoriesMap, setQcSubcategoriesMap] = useState({});

  const [editingAgentId, setEditingAgentId] = useState(null);

  // Refetch when page changes, filters applied, or sort changes
  useEffect(() => {
    const isInitial = !hasLoadedOnceRef.current;
    hasLoadedOnceRef.current = true;
    fetchData(isInitial);
  }, [currentPage, pageSize, filters.dateFrom, filters.dateTo, filters.dataSource, filters.verdict, filters.lifecycle, filters.agent, filters.subCategory, filters.product, filters.brand, filters.competitorBrand, filters.buyingStage, filters.qcCategory, filters.qcSubcategory, filters.tier, debouncedSearch, groupSort.key, groupSort.order]);

  // Update filters when URL params change
  useEffect(() => {
    const verdictParam = searchParams.get('verdict');
    const agentParam = searchParams.get('agent');
    const dateFromParam = searchParams.get('dateFrom');
    const dateToParam = searchParams.get('dateTo');
    const subCategoryParam = searchParams.get('subCategory');
    const productParam = searchParams.get('product');
    const brandParam = searchParams.get('brand');
    const competitorBrandParam = searchParams.get('competitorBrand');
    const buyingStageParam = searchParams.get('buyingStage');
    const dataSourceParam = searchParams.get('dataSource');
    const qcCategoryParam = searchParams.get('qcCategory');
    const qcSubcategoryParam = searchParams.get('qcSubcategory');
    const tierParam = searchParams.get('tier');
    const stageParam = searchParams.get('stage');
    const pageParam = searchParams.get('page');

    setFilters({
      verdict: verdictParam || '',
      lifecycle: stageParam || '',
      agent: agentParam || '',
      dateFrom: dateFromParam || '',
      dateTo: dateToParam || '',
      subCategory: subCategoryParam || '',
      product: productParam || '',
      brand: brandParam || '',
      competitorBrand: competitorBrandParam || '',
      buyingStage: buyingStageParam || '',
      dataSource: dataSourceParam || '',
      qcCategory: qcCategoryParam || '',
      qcSubcategory: qcSubcategoryParam || '',
      tier: tierParam || '',
    });
    
    // Restore page from URL if present
    if (pageParam) {
      const page = parseInt(pageParam, 10);
      if (page > 0) setCurrentPage(page);
    }
  }, [searchParams]);

  useEffect(() => {
    applyFilters();
  }, [calls, filters, searchQuery, sortConfig]);

  useEffect(() => {
    setShowBulkActions(selectedCalls.size > 0);
    // Reset the phones-highlight cache whenever the selection is cleared,
    // so no stale row highlight lingers after "Cancel" / bulk actions.
    if (selectedCalls.size === 0 && selectedPhones.size > 0) {
      setSelectedPhones(new Set());
    }
  }, [selectedCalls]);

  // Check if drill-down filters are active (these require fetching all data)
  const hasDrillDownFilters = filters.subCategory || filters.product || filters.brand || filters.competitorBrand || filters.buyingStage || filters.lifecycle;

  // Monotonic request id — only the latest fetch updates state
  const fetchSeqRef = useRef(0);

  const fetchData = async (initial = false) => {
    const reqId = ++fetchSeqRef.current;
    if (initial) setLoading(true);
    else setIsRefetching(true);
    try {
      // Always fetch grouped data
      let url = `${API}/calls/grouped?page=${currentPage}&limit=${pageSize}&sort_by=${groupSort.key}&sort_order=${groupSort.order}`;
      if (filters.dateFrom) url += `&date_from=${filters.dateFrom}`;
      if (filters.dateTo) url += `&date_to=${filters.dateTo}`;
      if (filters.dataSource) url += `&data_source=${encodeURIComponent(filters.dataSource)}`;
      if (filters.verdict) url += `&verdict=${encodeURIComponent(filters.verdict)}`;
      if (filters.lifecycle) url += `&lifecycle=${encodeURIComponent(filters.lifecycle)}`;
      if (filters.agent) url += `&agent_id=${encodeURIComponent(filters.agent)}`;
      if (filters.subCategory) url += `&sub_category=${encodeURIComponent(filters.subCategory)}`;
      if (filters.product) url += `&product=${encodeURIComponent(filters.product)}`;
      if (filters.brand) url += `&brand=${encodeURIComponent(filters.brand)}`;
      if (filters.competitorBrand) url += `&competitor_brand=${encodeURIComponent(filters.competitorBrand)}`;
      if (filters.buyingStage) url += `&buying_stage=${encodeURIComponent(filters.buyingStage)}`;
      if (filters.qcCategory) url += `&qc_category=${encodeURIComponent(filters.qcCategory)}`;
      if (filters.qcSubcategory) url += `&qc_subcategory=${encodeURIComponent(filters.qcSubcategory)}`;
      if (filters.tier) url += `&tier=${encodeURIComponent(filters.tier)}`;
      if (debouncedSearch && debouncedSearch.trim()) url += `&q=${encodeURIComponent(debouncedSearch.trim())}`;
      
      const [groupedRes, agentsRes, sourcesRes, qcRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/agents`),
        axios.get(`${API}/data-sources`),
        axios.get(`${API}/qc-categories`),
      ]);
      // Ignore stale responses (a newer request was kicked off while this one was in flight)
      if (reqId !== fetchSeqRef.current) return;
      setGroupedData(groupedRes.data.groups || []);
      setGroupedTotal(groupedRes.data.total || 0);
      setTotalPages(groupedRes.data.total_pages || 1);
      setTotalCalls(groupedRes.data.total_calls || 0);
      setAgents(agentsRes.data);
      setDataSources(sourcesRes.data || []);
      setQcCategories(qcRes.data?.categories || []);
      setQcSubcategoriesMap(qcRes.data?.subcategories || {});
    } catch (error) {
      if (reqId !== fetchSeqRef.current) return; // ignore stale errors
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      if (reqId === fetchSeqRef.current) {
        setLoading(false);
        setIsRefetching(false);
      }
    }
  };
  
  // Fetch calls for a specific seller when expanded
  const fetchSellerCalls = async (phone) => {
    if (sellerCalls[phone]) return sellerCalls[phone]; // Already loaded
    
    setLoadingSellerCalls(prev => new Set([...prev, phone]));
    try {
      const response = await axios.get(`${API}/calls/by-phone/${encodeURIComponent(phone)}?sort_order=desc`);
      const fetched = response.data.calls || [];
      setSellerCalls(prev => ({ ...prev, [phone]: fetched }));
      return fetched;
    } catch (error) {
      console.error('Error fetching seller calls:', error);
      toast.error('Failed to load calls');
      return [];
    } finally {
      setLoadingSellerCalls(prev => {
        const next = new Set(prev);
        next.delete(phone);
        return next;
      });
    }
  };
  
  const toggleSellerExpanded = (phone) => {
    const next = new Set(expandedSellers);
    if (next.has(phone)) {
      next.delete(phone);
    } else {
      next.add(phone);
      fetchSellerCalls(phone);
    }
    setExpandedSellers(next);
  };

  const applyFilters = () => {
    let filtered = [...calls];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.filename?.toLowerCase().includes(query) ||
        c.summary?.toLowerCase().includes(query) ||
        c.key_topics?.some(t => t.toLowerCase().includes(query)) ||
        c.product_mentions?.some(p => p.toLowerCase().includes(query)) ||
        c.brand_mentions?.some(b => b.toLowerCase().includes(query))
      );
    }

    if (filters.verdict) {
      // The 'unanalysed' sentinel matches calls with no final_verdict set,
      // mirroring the backend's ?verdict=unanalysed handling on
      // /api/calls/grouped.
      if (filters.verdict === 'unanalysed') {
        filtered = filtered.filter(c => !c.final_verdict);
      } else {
        filtered = filtered.filter(c => c.final_verdict === filters.verdict);
      }
    }
    if (filters.lifecycle) filtered = filtered.filter(c => c.lifecycle_stage === filters.lifecycle);
    if (filters.agent) filtered = filtered.filter(c => c.agent_id === filters.agent);
    if (filters.dateFrom) filtered = filtered.filter(c => new Date(c.call_date) >= new Date(filters.dateFrom));
    if (filters.dateTo) filtered = filtered.filter(c => new Date(c.call_date) <= new Date(filters.dateTo));
    
    // Filter by sub-category (interest driver or rejection reason) - case-insensitive
    // For Not Interested: check primary_reason, secondary_reasons, AND key_objections
    if (filters.subCategory) {
      const filterLower = filters.subCategory.toLowerCase();
      filtered = filtered.filter(c => {
        // For Interested calls - check drivers
        const interestedDrivers = c.interested_analysis?.drivers || [];
        const driverMatch = interestedDrivers.some(d => d?.toLowerCase() === filterLower);
        
        // For Not Interested calls - check primary_reason, secondary_reasons, AND key_objections
        const primaryReason = c.not_interested_analysis?.primary_reason || '';
        const secondaryReasons = c.not_interested_analysis?.secondary_reasons || [];
        const keyObjections = c.key_objections || [];
        
        const primaryMatch = primaryReason.toLowerCase() === filterLower;
        const secondaryMatch = secondaryReasons.some(r => r?.toLowerCase() === filterLower);
        const objectionMatch = keyObjections.some(o => o?.toLowerCase() === filterLower);
        
        return driverMatch || primaryMatch || secondaryMatch || objectionMatch;
      });
    }
    
    // Filter by product mention
    if (filters.product) {
      filtered = filtered.filter(c => 
        c.product_mentions?.some(p => p.toLowerCase().includes(filters.product.toLowerCase()))
      );
    }
    
    // Filter by brand mention
    if (filters.brand) {
      filtered = filtered.filter(c => 
        c.brand_mentions?.some(b => b.toLowerCase().includes(filters.brand.toLowerCase()))
      );
    }
    
    // Filter by competitor brand mention
    if (filters.competitorBrand) {
      filtered = filtered.filter(c => 
        c.competitor_brand_mentions?.some(b => b.toLowerCase().includes(filters.competitorBrand.toLowerCase()))
      );
    }
    
    // Filter by buying stage
    if (filters.buyingStage) {
      const filterLower = filters.buyingStage.toLowerCase();
      filtered = filtered.filter(c => {
        const callStage = c.interested_analysis?.buying_stage || c.call_stage || '';
        return callStage.toLowerCase() === filterLower;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal, bVal;
        
        switch (sortConfig.key) {
          case 'filename':
            aVal = a.filename || '';
            bVal = b.filename || '';
            break;
          case 'analyzed_at':
            aVal = new Date(a.analyzed_at || a.uploaded_at || 0);
            bVal = new Date(b.analyzed_at || b.uploaded_at || 0);
            break;
          case 'final_verdict':
            aVal = a.final_verdict || '';
            bVal = b.final_verdict || '';
            break;
          case 'lifecycle_stage':
            aVal = a.lifecycle_stage || '';
            bVal = b.lifecycle_stage || '';
            break;
          default:
            aVal = a[sortConfig.key] || '';
            bVal = b[sortConfig.key] || '';
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredCalls(filtered);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 text-slate-300" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-[#2eb4ac]" /> 
      : <ArrowDown className="w-3 h-3 text-[#2eb4ac]" />;
  };

  // Sync filters to URL for proper browser history
  const updateFiltersWithUrl = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to page 1 when filters change
    
    // Build URL params from filters
    const params = new URLSearchParams();
    if (newFilters.verdict) params.set('verdict', newFilters.verdict);
    if (newFilters.lifecycle) params.set('stage', newFilters.lifecycle);
    if (newFilters.agent) params.set('agent', newFilters.agent);
    if (newFilters.dateFrom) params.set('dateFrom', newFilters.dateFrom);
    if (newFilters.dateTo) params.set('dateTo', newFilters.dateTo);
    if (newFilters.subCategory) params.set('subCategory', newFilters.subCategory);
    if (newFilters.product) params.set('product', newFilters.product);
    if (newFilters.brand) params.set('brand', newFilters.brand);
    if (newFilters.competitorBrand) params.set('competitorBrand', newFilters.competitorBrand);
    if (newFilters.buyingStage) params.set('buyingStage', newFilters.buyingStage);
    if (newFilters.dataSource) params.set('dataSource', newFilters.dataSource);
    if (newFilters.qcCategory) params.set('qcCategory', newFilters.qcCategory);
    if (newFilters.qcSubcategory) params.set('qcSubcategory', newFilters.qcSubcategory);
    if (newFilters.tier) params.set('tier', newFilters.tier);
    // Page is reset to 1, so don't include in URL
    
    // Preserve dev_bypass if present
    if (searchParams.get('dev_bypass')) params.set('dev_bypass', 'true');
    
    // Update URL without full page reload
    setSearchParams(params, { replace: false });
  };

  const clearFilters = () => {
    const emptyFilters = { verdict: '', lifecycle: '', agent: '', dateFrom: '', dateTo: '', subCategory: '', product: '', brand: '', competitorBrand: '', buyingStage: '', dataSource: '', qcCategory: '', qcSubcategory: '', tier: '' };
    setFilters(emptyFilters);
    setSearchQuery('');
    setCurrentPage(1); // Reset to page 1
    
    // Clear URL params (keep dev_bypass if present)
    const params = new URLSearchParams();
    if (searchParams.get('dev_bypass')) params.set('dev_bypass', 'true');
    setSearchParams(params, { replace: false });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '') || searchQuery !== '';
  
  // Get readable filter labels for display
  const getActiveFilterLabels = () => {
    const labels = [];
    if (filters.tier) labels.push({ type: 'Tier', value: filters.tier, key: 'tier' });
    if (filters.qcCategory) labels.push({ type: 'Category', value: filters.qcCategory, key: 'qcCategory' });
    if (filters.qcSubcategory) labels.push({ type: 'Sub-category', value: filters.qcSubcategory, key: 'qcSubcategory' });
    if (filters.subCategory) labels.push({ type: 'Reason', value: filters.subCategory, key: 'subCategory' });
    if (filters.product) labels.push({ type: 'Product', value: filters.product, key: 'product' });
    if (filters.brand) labels.push({ type: 'Brand', value: filters.brand, key: 'brand' });
    if (filters.competitorBrand) labels.push({ type: 'Competitor', value: filters.competitorBrand, key: 'competitorBrand' });
    if (filters.buyingStage) labels.push({ type: 'Buying Stage', value: filters.buyingStage, key: 'buyingStage' });
    return labels;
  };
  
  const activeFilterLabels = getActiveFilterLabels();

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState(null);
  const [highlightedCallId, setHighlightedCallId] = useState(null);

  // Restore "last opened call" highlight when returning from Call Detail
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('helix:lastOpenedCall');
      if (!raw) return;
      const { callId, phone, ts } = JSON.parse(raw);
      // Only honor recent ones (within 10 minutes)
      if (!callId || !phone || Date.now() - (ts || 0) > 10 * 60 * 1000) {
        sessionStorage.removeItem('helix:lastOpenedCall');
        return;
      }
      // Auto-expand the seller and fetch its calls, then highlight
      setExpandedSellers(prev => {
        const next = new Set(prev);
        next.add(phone);
        return next;
      });
      fetchSellerCalls(phone).then(() => {
        setHighlightedCallId(callId);
        // Scroll into view shortly after render
        setTimeout(() => {
          const el = document.querySelector(`[data-call-card-id="${callId}"]`);
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 350);
        // Fade out highlight after 4s
        setTimeout(() => setHighlightedCallId(null), 4000);
      });
      sessionStorage.removeItem('helix:lastOpenedCall');
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(null); // { total, done, analyzed, skipped, failed, currentBatch }

  const handleBulkAnalyze = async () => {
    if (selectedCalls.size === 0 || isAnalyzing) return;
    // Backend hard cap is 30 per request; UI also enforces via toggle handlers.
    const ids = Array.from(selectedCalls).slice(0, 30);
    setIsAnalyzing(true);
    setAnalyzeProgress({ total: ids.length, done: 0, analyzed: 0, skipped: 0, failed: 0, currentBatch: 0 });

    // Process in batches of 3 so the user sees progress while the LLM crunches
    const BATCH = 3;
    let totalAnalyzed = 0, totalSkipped = 0, totalFailed = 0;
    try {
      for (let i = 0; i < ids.length; i += BATCH) {
        const chunk = ids.slice(i, i + BATCH);
        setAnalyzeProgress(p => p ? { ...p, currentBatch: chunk.length, done: i } : p);
        try {
          const res = await axios.post(`${API}/calls/bulk-analyze`, chunk);
          const a = res.data?.analyzed?.length || 0;
          const s = res.data?.skipped?.length || 0;
          const f = res.data?.failed?.length || 0;
          totalAnalyzed += a; totalSkipped += s; totalFailed += f;
          setAnalyzeProgress(p => p ? { ...p, done: i + chunk.length, analyzed: totalAnalyzed, skipped: totalSkipped, failed: totalFailed } : p);
        } catch (err) {
          totalFailed += chunk.length;
          setAnalyzeProgress(p => p ? { ...p, done: i + chunk.length, failed: totalFailed } : p);
        }
      }
      let msg = `${totalAnalyzed} analyzed`;
      if (totalSkipped) msg += ` · ${totalSkipped} already analyzed`;
      if (totalFailed) msg += ` · ${totalFailed} failed`;
      toast.success(msg);
      setSelectedCalls(new Set());
      setSelectedPhones(new Set());
      // Refresh every currently-expanded seller so the row updates in-place
      // with the fresh analysis — no need for the user to collapse/re-open.
      const expanded = Array.from(expandedSellers);
      if (expanded.length > 0) {
        const refreshed = await Promise.all(
          expanded.map(async (phone) => {
            try {
              const res = await axios.get(`${API}/calls/by-phone/${encodeURIComponent(phone)}?sort_order=desc`);
              return [phone, res.data.calls || []];
            } catch {
              return [phone, []];
            }
          })
        );
        setSellerCalls(prev => {
          const next = { ...prev };
          for (const [phone, calls] of refreshed) {
            next[phone] = calls;
          }
          return next;
        });
      } else {
        // No sellers expanded — safe to drop the stale cache entirely.
        setSellerCalls({});
      }
      fetchData();
    } finally {
      // Keep the modal up briefly so user sees the final state, then close
      setTimeout(() => {
        setIsAnalyzing(false);
        setAnalyzeProgress(null);
      }, 1200);
    }
  };

  const toggleSelectAll = () => {
    if (selectedCalls.size === filteredCalls.length) {
      setSelectedCalls(new Set());
    } else {
      setSelectedCalls(new Set(filteredCalls.map(c => c.id)));
    }
  };

  const toggleSelectCall = (callId) => {
    const newSelected = new Set(selectedCalls);
    if (newSelected.has(callId)) {
      newSelected.delete(callId);
    } else {
      if (newSelected.size >= 30) {
        toast.error('Maximum 30 calls can be selected at once');
        return;
      }
      newSelected.add(callId);
    }
    setSelectedCalls(newSelected);
  };

  // Toggle all calls under a single seller phone (works against fetched seller calls)
  const toggleSelectSeller = (phone) => {
    const callsForSeller = sellerCalls[phone] || [];
    if (callsForSeller.length === 0) return; // not loaded yet
    const allSelected = callsForSeller.every(c => selectedCalls.has(c.id));
    const next = new Set(selectedCalls);
    if (allSelected) {
      callsForSeller.forEach(c => next.delete(c.id));
      setSelectedPhones(prev => {
        const n = new Set(prev);
        n.delete(phone);
        return n;
      });
    } else {
      for (const c of callsForSeller) {
        if (next.size >= 30) break;
        next.add(c.id);
      }
      if (callsForSeller.some(c => !next.has(c.id))) {
        toast.error('Selection capped at 30 calls');
      }
      setSelectedPhones(prev => {
        const n = new Set(prev);
        n.add(phone);
        return n;
      });
    }
    setSelectedCalls(next);
  };

  // Resolve the underlying call IDs for a set of seller phones and store them
  // in selectedCalls (respecting the 30-call bulk cap). Selection state is
  // driven by phones, but bulk actions still act on call IDs.
  const resolveCallIdsForPhones = async (phones) => {
    if (!phones || phones.size === 0) {
      setSelectedCalls(new Set());
      return { ids: [], truncated: false };
    }
    try {
      const params = new URLSearchParams();
      if (filters.verdict) params.set('verdict', filters.verdict);
      if (filters.lifecycle) params.set('lifecycle_stage', filters.lifecycle);
      if (filters.agent) params.set('agent_id', filters.agent);
      if (filters.dateFrom) params.set('date_from', filters.dateFrom);
      if (filters.dateTo) params.set('date_to', filters.dateTo);
      if (filters.dataSource) params.set('data_source', filters.dataSource);
      if (filters.buyingStage) params.set('buying_stage', filters.buyingStage);
      if (filters.qcCategory) params.set('qc_category', filters.qcCategory);
      if (filters.qcSubcategory) params.set('qc_subcategory', filters.qcSubcategory);
      if (filters.tier) params.set('tier', filters.tier);
      if (debouncedSearch && debouncedSearch.trim()) params.set('q', debouncedSearch.trim());
      params.set('phones', Array.from(phones).join(','));
      params.set('limit', '30');
      const res = await axios.get(`${API}/calls-ids-by-filters?${params.toString()}`);
      const ids = res.data?.call_ids || [];
      setSelectedCalls(new Set(ids));
      return { ids, truncated: ids.length >= 30 };
    } catch {
      toast.error('Failed to resolve selection');
      return { ids: [], truncated: false };
    }
  };

  // Toggle a single seller row's selection — visual state driven by phone.
  const toggleSellerRow = (phone) => {
    const nextPhones = new Set(selectedPhones);
    if (nextPhones.has(phone)) {
      nextPhones.delete(phone);
    } else {
      nextPhones.add(phone);
    }
    setSelectedPhones(nextPhones);
    resolveCallIdsForPhones(nextPhones);
  };

  // Master "Select All" at the header — toggles every row on the current page.
  const toggleSelectAllOnPage = async () => {
    const pagePhones = groupedData.map(g => g.phone);
    const allSelected = pagePhones.length > 0 && pagePhones.every(p => selectedPhones.has(p));
    if (allSelected) {
      setSelectedPhones(new Set());
      setSelectedCalls(new Set());
      return;
    }
    const nextPhones = new Set(pagePhones);
    setSelectedPhones(nextPhones);
    const { truncated } = await resolveCallIdsForPhones(nextPhones);
    toast.success(
      truncated
        ? `${pagePhones.length} rows selected · analysis capped at first 30 calls`
        : `${pagePhones.length} row${pagePhones.length !== 1 ? 's' : ''} selected on this page`
    );
  };

  const handleBulkDelete = () => {
    if (selectedCalls.size === 0) return;
    setConfirmModal({
      title: `Delete ${selectedCalls.size} call${selectedCalls.size !== 1 ? 's' : ''}?`,
      message: `This will permanently remove ${selectedCalls.size} call${selectedCalls.size !== 1 ? 's' : ''} and the associated transcripts. This action cannot be undone.`,
      confirmLabel: `Delete ${selectedCalls.size}`,
      tone: 'danger',
      onConfirm: async () => {
        try {
          await axios.post(`${API}/calls/bulk-delete`, Array.from(selectedCalls));
          toast.success(`${selectedCalls.size} call(s) deleted`);
          setSelectedCalls(new Set());
          // Also clear cached seller calls so expanded rows refresh
          setSellerCalls({});
          fetchData();
        } catch (error) {
          toast.error('Failed to delete calls');
        } finally {
          setConfirmModal(null);
        }
      },
    });
  };

  const handleClearAll = () => {
    setConfirmModal({
      title: `Clear ALL call logs?`,
      message: `This will permanently delete every call log in the system (${groupedTotal} sellers / all their calls). Transcripts and analyses will be lost. This action cannot be undone.`,
      confirmLabel: 'Clear everything',
      tone: 'danger',
      requireTypedConfirm: 'DELETE',
      onConfirm: async () => {
        try {
          const res = await axios.delete(`${API}/calls`, { params: { confirm: 'DELETE_ALL' } });
          toast.success(res.data?.message || 'All calls cleared');
          setSelectedCalls(new Set());
          setSellerCalls({});
          setExpandedSellers(new Set());
          fetchData();
        } catch (error) {
          toast.error('Failed to clear calls');
        } finally {
          setConfirmModal(null);
        }
      },
    });
  };

  const handleDeleteCall = (callId, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({
      title: 'Delete this call?',
      message: 'This will permanently remove the call, its transcript, and analysis. This cannot be undone.',
      confirmLabel: 'Delete call',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/calls/${callId}`);
          toast.success('Call deleted');
          setSellerCalls({});
          fetchData();
        } catch (error) {
          toast.error('Failed to delete call');
        } finally {
          setConfirmModal(null);
        }
      },
    });
  };

  const handleAssignAgent = async (callId, agentId) => {
    try {
      await axios.patch(`${API}/calls/${callId}/agent`, null, { params: { agent_id: agentId || null } });
      toast.success(agentId ? 'Agent assigned' : 'Agent removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to update agent');
    }
    setEditingAgentId(null);
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.dateFrom) params.set('date_from', filters.dateFrom);
      if (filters.dateTo) params.set('date_to', filters.dateTo);
      if (filters.dataSource) params.set('data_source', filters.dataSource);
      if (filters.verdict) params.set('verdict', filters.verdict);
      if (filters.lifecycle) params.set('lifecycle', filters.lifecycle);
      if (filters.agent) params.set('agent_id', filters.agent);
      if (filters.subCategory) params.set('sub_category', filters.subCategory);
      if (filters.product) params.set('product', filters.product);
      if (filters.brand) params.set('brand', filters.brand);
      if (filters.competitorBrand) params.set('competitor_brand', filters.competitorBrand);
      if (filters.buyingStage) params.set('buying_stage', filters.buyingStage);
      if (debouncedSearch) params.set('q', debouncedSearch);
      // Use the browser's current origin so the export contains shareable URLs
      // (avoids leaking internal cluster URLs into the CSV)
      params.set('base_url', window.location.origin);
      const qs = params.toString();
      const url = `${API}/calls/export/csv${qs ? `?${qs}` : ''}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `helix_calls_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const getAgentName = (agentId) => agents.find(a => a.id === agentId)?.name || null;

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // Get current sub-category for a call - if filtering, show the matched category
  const getSubCategory = (call) => {
    // If we're filtering by subCategory, find and return the matching driver/reason
    if (filters.subCategory) {
      const filterLower = filters.subCategory.toLowerCase();
      
      if (call.final_verdict === 'Interested') {
        const drivers = call.interested_analysis?.drivers || [];
        const matchedDriver = drivers.find(d => d?.toLowerCase() === filterLower);
        if (matchedDriver) return matchedDriver;
      } else {
        // Check primary_reason
        const primaryReason = call.not_interested_analysis?.primary_reason || '';
        if (primaryReason.toLowerCase() === filterLower) return primaryReason;
        
        // Check secondary_reasons
        const secondaryReasons = call.not_interested_analysis?.secondary_reasons || [];
        const matchedSecondary = secondaryReasons.find(r => r?.toLowerCase() === filterLower);
        if (matchedSecondary) return matchedSecondary;
        
        // Check key_objections
        const keyObjections = call.key_objections || [];
        const matchedObjection = keyObjections.find(o => o?.toLowerCase() === filterLower);
        if (matchedObjection) return matchedObjection;
      }
    }
    
    // Default: return first driver or primary reason
    if (call.final_verdict === 'Interested') {
      return call.interested_analysis?.drivers?.[0] || null;
    } else {
      return call.not_interested_analysis?.primary_reason || null;
    }
  };

  // Generate navigation state for call detail links (preserves filters for back navigation)
  const getCallDetailLinkState = () => ({
    from: '/call-logs',
    filters: location.search // Preserve current query string
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="loading-call-logs">
        <div className="text-center">
          <BlobPreloader size={88} label="Loading call logs..." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full" data-testid="call-logs-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-slate-900">Call Logs</h1>
            {/* Subtle Total Sellers Badge */}
            <div className="relative flex items-center" data-testid="total-sellers-badge">
              <span className="absolute -inset-1 bg-[#2eb4ac]/20 rounded-full animate-pulse"></span>
              <span className="relative inline-flex items-center gap-1 px-2.5 py-0.5 bg-[#2eb4ac]/10 text-[#2eb4ac] text-sm font-semibold rounded-full">
                {groupedTotal} sellers
              </span>
            </div>
            {/* Total Calls Badge */}
            <div className="relative flex items-center" data-testid="total-calls-badge">
              <span className="relative inline-flex items-center gap-1 px-2.5 py-0.5 bg-purple-50 text-purple-700 text-sm font-semibold rounded-full">
                {totalCalls} calls
              </span>
            </div>
          </div>
          <p className="text-slate-500 text-sm mt-0.5">Calls grouped by seller · Click to expand</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={handleClearAll}
            disabled={groupedTotal === 0}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-rose-200 text-rose-600 text-sm font-medium rounded-lg hover:bg-rose-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="clear-all-btn"
          >
            <Trash2 className="w-4 h-4" />
            Clear All
          </button>
        </div>
      </div>

      {/* Filters Bar - Always Visible */}
      <div className="bg-white rounded-xl border border-slate-200 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-slate-500">
            <SlidersHorizontal className="w-4 h-4" />
            <span className="text-sm font-medium">Filters</span>
          </div>
          
          {/* Search — fires on Enter */}
          <div className="relative flex-1 min-w-[200px] max-w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitSearch();
                }
              }}
              placeholder="Search name, phone, seller code — press Enter"
              className="w-full pl-9 pr-9 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac]"
              data-testid="search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => { setSearchQuery(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                title="Clear search"
                data-testid="clear-search-btn"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          
          {/* Lead Status */}
          <select 
            value={filters.verdict} 
            onChange={(e) => updateFiltersWithUrl({...filters, verdict: e.target.value})}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac] bg-white min-w-[120px]"
            data-testid="verdict-filter"
          >
            <option value="">All Status</option>
            <option value="Interested">Interested</option>
            <option value="Not Interested">Not Interested</option>
            <option value="unanalysed">Not Analysed</option>
          </select>
          
          {/* Lead Stage */}
          <select 
            value={filters.lifecycle} 
            onChange={(e) => updateFiltersWithUrl({...filters, lifecycle: e.target.value})}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac] bg-white min-w-[100px]"
            data-testid="lifecycle-filter"
          >
            <option value="">All Stages</option>
            <option value="Hot">Hot</option>
            <option value="Warm">Warm</option>
            <option value="Cold">Cold</option>
          </select>
          
          {/* Agent */}
          <select 
            value={filters.agent} 
            onChange={(e) => updateFiltersWithUrl({...filters, agent: e.target.value})}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac] bg-white min-w-[130px]"
            data-testid="agent-filter"
          >
            <option value="">All Agents</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>

          {/* Data Source */}
          <select 
            value={filters.dataSource} 
            onChange={(e) => updateFiltersWithUrl({...filters, dataSource: e.target.value})}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac] bg-white min-w-[130px]"
            data-testid="data-source-filter"
          >
            <option value="">All Sources</option>
            {dataSources.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>

          {/* Call Category */}
          <select
            value={filters.qcCategory}
            onChange={(e) => updateFiltersWithUrl({...filters, qcCategory: e.target.value, qcSubcategory: ''})}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac] bg-white min-w-[160px]"
            data-testid="qc-category-filter"
          >
            <option value="">All Categories</option>
            {qcCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          {/* Sub-Category — populates dynamically from selected Category */}
          <select
            value={filters.qcSubcategory}
            onChange={(e) => updateFiltersWithUrl({...filters, qcSubcategory: e.target.value})}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac] bg-white min-w-[180px] disabled:opacity-50"
            data-testid="qc-subcategory-filter"
            disabled={!filters.qcCategory}
            title={filters.qcCategory ? 'Filter by sub-category' : 'Pick a Category first'}
          >
            <option value="">All Sub-categories</option>
            {(qcSubcategoriesMap[filters.qcCategory] || []).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          
          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input 
              type="date" 
              value={filters.dateFrom} 
              onChange={(e) => updateFiltersWithUrl({...filters, dateFrom: e.target.value})}
              className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac]"
              data-testid="date-from"
            />
            <span className="text-slate-400 text-sm">to</span>
            <input 
              type="date" 
              value={filters.dateTo} 
              onChange={(e) => updateFiltersWithUrl({...filters, dateTo: e.target.value})}
              className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac]"
              data-testid="date-to"
            />
          </div>
          
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-2 py-1.5 text-sm text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
              data-testid="clear-filters-btn"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>
        
        {/* Active Drill-Down Filter Tags */}
        {activeFilterLabels.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
            <span className="text-xs text-slate-500 font-medium">Filtered by:</span>
            {activeFilterLabels.map((filter, idx) => (
              <span 
                key={idx}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#2eb4ac]/10 text-[#2eb4ac] text-xs font-medium rounded-full"
              >
                <span className="text-[#2eb4ac]/60">{filter.type}:</span>
                {filter.value}
                <button 
                  onClick={() => {
                    updateFiltersWithUrl({ ...filters, [filter.key]: '' });
                  }}
                  className="ml-0.5 hover:text-[#259790]"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-slate-900 text-white rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5 text-[#2eb4ac]" />
            <span className="font-medium">{selectedCalls.size} call(s) selected</span>
            {selectedCalls.size > 30 && (
              <span className="text-xs text-amber-300">Only the first 30 will be analyzed</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBulkEditModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-lg transition-colors"
              data-testid="bulk-edit-btn"
            >
              <Filter className="w-4 h-4" /> Update Selected
            </button>
            <button
              onClick={handleBulkAnalyze}
              disabled={isAnalyzing}
              className="flex items-center gap-2 px-4 py-2 bg-[#2eb4ac] hover:bg-[#259790] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
              data-testid="bulk-analyze-btn"
            >
              {isAnalyzing ? 'Analyzing…' : (<><Brain className="w-4 h-4" /> Analyze Selected</>)}
            </button>
            <button onClick={handleBulkDelete} className="flex items-center gap-2 px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white font-medium rounded-lg transition-colors">
              <Trash2 className="w-4 h-4" /> Delete Selected
            </button>
            <button onClick={() => setSelectedCalls(new Set())} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Grouped Calls Table with Expandable Rows */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden relative">
        {/* Thin progress bar at the top when refetching (search / filter / sort) */}
        {isRefetching && (
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-slate-100 overflow-hidden z-20" data-testid="table-refetch-bar">
            <div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-[#2eb4ac] to-transparent" style={{ animation: 'top-bar-slide 1.1s ease-in-out infinite' }} />
          </div>
        )}
        <div className="overflow-x-auto max-h-[calc(100vh-320px)] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="w-10 px-3 py-3 bg-slate-50">
                  {/* Master page-scoped select checkbox — selects every seller row on the current page */}
                  {(() => {
                    const pagePhones = groupedData.map(g => g.phone);
                    const totalOnPage = pagePhones.length;
                    const selectedOnPage = pagePhones.filter(p => selectedPhones.has(p)).length;
                    const allSelected = totalOnPage > 0 && selectedOnPage === totalOnPage;
                    const someSelected = selectedOnPage > 0 && !allSelected;
                    return (
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-slate-300 text-[#2eb4ac] focus:ring-2 focus:ring-[#2eb4ac]/30 cursor-pointer"
                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                        checked={allSelected}
                        onChange={() => {}}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelectAllOnPage();
                        }}
                        title={allSelected ? 'Clear selection' : 'Select all rows on this page'}
                        data-testid="select-all-page-header"
                        disabled={totalOnPage === 0}
                      />
                    );
                  })()}
                </th>
                <th className="w-10 px-3 py-3 bg-slate-50"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  <button 
                    onClick={() => setGroupSort(s => ({ key: 'phone', order: s.key === 'phone' && s.order === 'asc' ? 'desc' : 'asc' }))}
                    className="inline-flex items-center gap-1 hover:text-slate-700 transition-colors"
                    data-testid="sort-phone-seller"
                  >
                    Phone / Seller
                    {groupSort.key === 'phone' ? (
                      groupSort.order === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  <button 
                    onClick={() => setGroupSort(s => ({ key: 'total_calls', order: s.key === 'total_calls' && s.order === 'desc' ? 'asc' : 'desc' }))}
                    className="inline-flex items-center gap-1 hover:text-slate-700 transition-colors mx-auto"
                    data-testid="sort-calls"
                  >
                    Calls
                    {groupSort.key === 'total_calls' ? (
                      groupSort.order === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Breakdown</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  <button 
                    onClick={() => setGroupSort(s => ({ key: 'overall_verdict', order: s.key === 'overall_verdict' && s.order === 'desc' ? 'asc' : 'desc' }))}
                    className="inline-flex items-center gap-1 hover:text-slate-700 transition-colors"
                    data-testid="sort-verdict"
                  >
                    Overall Verdict
                    {groupSort.key === 'overall_verdict' ? (
                      groupSort.order === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Source</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Latest Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                  <button 
                    onClick={() => setGroupSort(s => ({ key: 'latest_call_date', order: s.key === 'latest_call_date' && s.order === 'desc' ? 'asc' : 'desc' }))}
                    className="inline-flex items-center gap-1 hover:text-slate-700 transition-colors"
                    data-testid="sort-latest-call"
                  >
                    Latest Call
                    {groupSort.key === 'latest_call_date' ? (
                      groupSort.order === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleGroups.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-12 text-center text-slate-500">
                    No sellers found
                  </td>
                </tr>
              ) : (
                visibleGroups.map((group) => {
                  const isRowSelected = selectedPhones.has(group.phone);
                  return (
                  <React.Fragment key={group.phone}>
                    {/* Seller Row */}
                    <tr 
                      onClick={() => toggleSellerExpanded(group.phone)}
                      className={`cursor-pointer transition-colors ${isRowSelected ? 'bg-[#2eb4ac]/5 hover:bg-[#2eb4ac]/10 border-l-2 border-l-[#2eb4ac]' : 'hover:bg-slate-50'}`}
                      data-testid={`seller-row-${group.phone}`}
                      data-selected={isRowSelected}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-slate-300 text-[#2eb4ac] focus:ring-2 focus:ring-[#2eb4ac]/30 cursor-pointer"
                          checked={isRowSelected}
                          onChange={() => {}}
                          onClick={(e) => { e.stopPropagation(); toggleSellerRow(group.phone); }}
                          title={isRowSelected ? 'Deselect this row' : 'Select this row'}
                          data-testid={`select-seller-${group.phone}`}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedSellers.has(group.phone) ? 'rotate-180' : ''}`} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                            <Phone className="w-5 h-5 text-slate-500" />
                          </div>
                          <div className="min-w-0">
                            {(() => {
                              // Resolve the best human label for this seller row.
                              // Priority: detected/filename customer name (the actual person on the call)
                              // → registered seller name (typically the store/company) → phone.
                              const primaryName = ((group.customer_names && group.customer_names.length) ? group.customer_names[0] : '')
                                || group.seller_name
                                || '';
                              // display_phone is the user-facing phone string;
                              // group.phone may be a synthetic 'name:<name>' lookup key.
                              const shownPhone = group.display_phone || group.phone || '';
                              const isSynthetic = (group.phone || '').startsWith('name:');
                              if (primaryName) {
                                return (
                                  <>
                                    <p
                                      className="font-medium text-slate-900 truncate"
                                      title={primaryName}
                                      data-testid={`row-customer-name-${group.phone}`}
                                    >
                                      {primaryName}
                                      {group.customer_names && group.customer_names.length > 1 && (
                                        <span className="ml-1 text-[10px] font-normal text-slate-400">
                                          +{group.customer_names.length - 1}
                                        </span>
                                      )}
                                    </p>
                                    <p className="text-xs text-slate-500 truncate">
                                      {isSynthetic ? (
                                        <PhoneAttachInline
                                          name={primaryName}
                                          synthKey={group.phone}
                                          onAttached={() => fetchData()}
                                        />
                                      ) : (shownPhone || 'Unknown')}
                                      {group.seller_name && group.seller_name !== primaryName && ` · ${group.seller_name}`}
                                      {group.store_name && group.store_name !== primaryName && group.store_name !== group.seller_name && ` · ${group.store_name}`}
                                    </p>
                                  </>
                                );
                              }
                              return (
                                <p className="font-medium text-slate-900">{shownPhone || 'Unknown'}</p>
                              );
                            })()}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {group.seller_status === 'existing' ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                            <Store className="w-3 h-3" />
                            Existing · {group.seller_id}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">
                            <User className="w-3 h-3" />
                            New Seller
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-lg font-bold text-slate-700">
                          {group.total_calls}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">
                            <ThumbsUp className="w-3 h-3" />
                            {group.interested_count}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-50 text-rose-700 text-xs font-medium rounded-full">
                            <ThumbsDown className="w-3 h-3" />
                            {group.not_interested_count}
                          </span>
                          {group.unanalysed_count > 0 && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-medium rounded-full"
                              title="Unanalysed calls"
                            >
                              <Clock className="w-3 h-3" />
                              {group.unanalysed_count}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {group.overall_verdict === 'Interested' ? (
                          <span className="px-3 py-1.5 bg-emerald-100 text-emerald-800 text-sm font-semibold rounded-lg">
                            Interested
                          </span>
                        ) : group.overall_verdict === 'Not Interested' ? (
                          <span className="px-3 py-1.5 bg-rose-100 text-rose-800 text-sm font-semibold rounded-lg">
                            Not Interested
                          </span>
                        ) : (
                          <span className="px-3 py-1.5 bg-amber-100 text-amber-800 text-sm font-semibold rounded-lg">
                            Not Analysed
                          </span>
                        )}
                      </td>
                      {/* Source column */}
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {(group.data_sources || []).length === 0 ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {group.data_sources.slice(0, 2).map((s) => (
                              <span key={s} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[11px] font-medium rounded-full">
                                {s}
                              </span>
                            ))}
                            {group.data_sources.length > 2 && (
                              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[11px] font-medium rounded-full">
                                +{group.data_sources.length - 2}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      {/* Latest Call Category + Sub-category + Remark */}
                      <td className="px-4 py-3">
                        {group.latest_qc_category ? (
                          <div className="flex flex-col gap-1 min-w-0 max-w-[220px]">
                            <span
                              className={`inline-flex w-fit px-2 py-0.5 text-[11px] font-medium rounded-full ring-1 ${
                                (QC_PALETTE[group.latest_qc_category]?.bg) || 'bg-slate-50'
                              } ${(QC_PALETTE[group.latest_qc_category]?.text) || 'text-slate-700'} ${(QC_PALETTE[group.latest_qc_category]?.ring) || 'ring-slate-100'}`}
                            >
                              {group.latest_qc_category}
                            </span>
                            {group.latest_qc_subcategory && (
                              <span
                                className="inline-flex w-fit px-2 py-0.5 text-[10px] font-medium rounded-full bg-slate-50 text-slate-600 ring-1 ring-slate-200"
                                title={`Sub-category: ${group.latest_qc_subcategory}`}
                                data-testid={`sub-category-pill-${group.phone}`}
                              >
                                {group.latest_qc_subcategory}
                              </span>
                            )}
                            {group.latest_qc_remark && (
                              <span className="text-[11px] text-slate-400 italic truncate" title={group.latest_qc_remark}>
                                {group.latest_qc_remark}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {group.latest_call_date && (
                          <div className="flex flex-col leading-tight">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-700">
                                {new Date(group.latest_call_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                              {group.latest_stage && (
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                  group.latest_stage === 'Hot' ? 'bg-orange-50 text-orange-700' :
                                  group.latest_stage === 'Warm' ? 'bg-yellow-50 text-yellow-700' :
                                  'bg-blue-50 text-blue-700'
                                }`}>
                                  {group.latest_stage}
                                </span>
                              )}
                            </div>
                            {group.latest_analyzed_at && (
                              <span
                                className="text-[10px] text-slate-400 mt-0.5"
                                title={`Analyzed ${new Date(group.latest_analyzed_at).toLocaleString()}`}
                              >
                                Analyzed {new Date(group.latest_analyzed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    
                    {/* Expanded Calls */}
                    {expandedSellers.has(group.phone) && (
                      <tr>
                        <td colSpan="11" className="p-0">
                          <div className="bg-slate-50 border-t border-b border-slate-200 px-6 py-6">
                            {loadingSellerCalls.has(group.phone) ? (
                              <div className="flex items-center justify-center py-10">
                                <div className="w-6 h-6 border-2 border-[#2eb4ac] border-t-transparent rounded-full animate-spin" />
                              </div>
                            ) : sellerCalls[group.phone]?.length > 0 ? (
                              <div>
                                <div className="flex items-center justify-between mb-5">
                                  <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em]">
                                    All Calls · {sellerCalls[group.phone].length}
                                  </p>
                                  <div className="flex items-center gap-2">
                                    {/* Select all calls for this seller */}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const ids = sellerCalls[group.phone].map(c => c.id);
                                        const next = new Set(selectedCalls);
                                        const allSelected = ids.every(id => next.has(id));
                                        if (allSelected) {
                                          ids.forEach(id => next.delete(id));
                                        } else {
                                          ids.forEach(id => next.add(id));
                                        }
                                        setSelectedCalls(next);
                                      }}
                                      className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-medium text-slate-600 hover:text-[#2eb4ac] hover:bg-[#2eb4ac]/5 rounded-md transition-colors"
                                      data-testid={`select-all-calls-${group.phone}`}
                                    >
                                      {sellerCalls[group.phone].every(c => selectedCalls.has(c.id))
                                        ? 'Deselect all calls'
                                        : 'Select all calls'}
                                    </button>
                                    {/* Re-analyze all calls for this seller */}
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        const ids = sellerCalls[group.phone].map(c => c.id);
                                        if (ids.length === 0) return;
                                        const ok = window.confirm(`Re-analyze all ${ids.length} call${ids.length > 1 ? 's' : ''} for this seller? Existing analysis will be overwritten.`);
                                        if (!ok) return;
                                        try {
                                          toast.message(`Re-analyzing ${ids.length} call${ids.length > 1 ? 's' : ''}…`);
                                          // bulk-analyze caps at 30 per call — chunk if needed
                                          for (let i = 0; i < ids.length; i += 30) {
                                            await axios.post(`${API}/calls/bulk-analyze?force=true`, ids.slice(i, i + 30));
                                          }
                                          toast.success('Re-analysis complete');
                                          const res = await axios.get(`${API}/calls/by-phone/${encodeURIComponent(group.phone)}?sort_order=desc`);
                                          setSellerCalls(prev => ({ ...prev, [group.phone]: res.data.calls || [] }));
                                          fetchData();
                                        } catch {
                                          toast.error('Failed to re-analyze');
                                        }
                                      }}
                                      className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-medium text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                                      data-testid={`reanalyze-all-${group.phone}`}
                                    >
                                      <RefreshCw className="w-3.5 h-3.5" />
                                      Re-analyze all
                                    </button>
                                  </div>
                                </div>
                                <div className="space-y-3">
                                  {sellerCalls[group.phone].map((call) => {
                                    const verdictPos = call.final_verdict === 'Interested';
                                    const verdictNeg = call.final_verdict === 'Not Interested';
                                    // Call timestamp = when the call actually happened (preserved from upload).
                                    // Analyzed timestamp = when AI ran on it (separate, set during analysis).
                                    const callTs = call.call_date || call.uploaded_at;
                                    const dateStr = callTs ? new Date(callTs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                                    const timeStr = callTs ? new Date(callTs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
                                    const analyzedStr = call.analyzed_at
                                      ? `${new Date(call.analyzed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${new Date(call.analyzed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
                                      : '';
                                    return (
                                      <div
                                        key={call.id}
                                        data-call-card-id={call.id}
                                        className={`group bg-white rounded-xl border transition-all ${
                                          highlightedCallId === call.id
                                            ? 'border-[#2eb4ac] ring-2 ring-[#2eb4ac]/40 shadow-[0_0_0_3px_rgba(46,180,172,0.18)] animate-pulse-once'
                                            : selectedCalls.has(call.id)
                                              ? 'border-[#2eb4ac] ring-1 ring-[#2eb4ac]/20 shadow-sm'
                                              : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                                        }`}
                                      >
                                        <div className="p-5">
                                          {/* Top row — meta + actions */}
                                          <div className="flex items-start justify-between gap-4 mb-4">
                                            <div className="flex items-center gap-4 min-w-0">
                                              <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded border-slate-300 text-[#2eb4ac] focus:ring-2 focus:ring-[#2eb4ac]/30 cursor-pointer flex-shrink-0"
                                                checked={selectedCalls.has(call.id)}
                                                onChange={() => toggleSelectCall(call.id)}
                                                onClick={(e) => e.stopPropagation()}
                                                data-testid={`select-call-${call.id}`}
                                              />
                                              {/* Date / time block */}
                                              <div className="flex flex-col leading-tight">
                                                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Call</span>
                                                <span className="text-sm font-semibold text-slate-900">{dateStr}</span>
                                                <span className="text-xs text-slate-500 mt-0.5">{timeStr}</span>
                                                {analyzedStr && (
                                                  <span
                                                    className="text-[10px] text-slate-400 mt-1.5 pt-1.5 border-t border-slate-100"
                                                    title={`AI analysis ran at ${new Date(call.analyzed_at).toLocaleString()}`}
                                                  >
                                                    <span className="font-semibold tracking-wider uppercase text-slate-400">Analyzed</span>
                                                    <br />
                                                    {analyzedStr}
                                                  </span>
                                                )}
                                              </div>
                                              {/* Vertical divider */}
                                              <span className="hidden md:block w-px h-8 bg-slate-200" />
                                              {/* Verdict + lifecycle + source pills */}
                                              <div className="flex flex-wrap items-center gap-2">
                                                <span className={`px-2.5 py-1 text-xs font-medium rounded-full ring-1 ${
                                                  verdictPos ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                                                  : verdictNeg ? 'bg-rose-50 text-rose-700 ring-rose-100'
                                                  : 'bg-amber-50 text-amber-700 ring-amber-100'
                                                }`}>
                                                  {verdictPos ? 'Interested' : verdictNeg ? 'Not Interested' : 'Not Analysed'}
                                                </span>
                                                {call.lifecycle_stage && (
                                                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ring-1 ${
                                                    call.lifecycle_stage === 'Hot'  ? 'bg-orange-50 text-orange-700 ring-orange-100' :
                                                    call.lifecycle_stage === 'Warm' ? 'bg-yellow-50 text-yellow-700 ring-yellow-100' :
                                                                                       'bg-blue-50 text-blue-700 ring-blue-100'
                                                  }`}>
                                                    {call.lifecycle_stage}
                                                  </span>
                                                )}
                                                {/* Inline Data Source dropdown */}
                                                <select
                                                  value={call.data_source || ''}
                                                  onClick={(e) => e.stopPropagation()}
                                                  onChange={async (e) => {
                                                    e.stopPropagation();
                                                    const newSrc = e.target.value;
                                                    try {
                                                      await axios.patch(`${API}/calls/${call.id}/data-source`, { data_source: newSrc });
                                                      setSellerCalls(prev => ({
                                                        ...prev,
                                                        [group.phone]: prev[group.phone].map(c => c.id === call.id ? { ...c, data_source: newSrc || null } : c)
                                                      }));
                                                      toast.success('Source updated');
                                                    } catch {
                                                      toast.error('Failed to update source');
                                                    }
                                                  }}
                                                  className="px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full ring-1 ring-purple-100 border-0 focus:outline-none focus:ring-2 focus:ring-purple-200 cursor-pointer"
                                                  data-testid={`data-source-${call.id}`}
                                                >
                                                  <option value="">Source —</option>
                                                  {dataSources.map(s => (
                                                    <option key={s.id} value={s.name}>{s.name}</option>
                                                  ))}
                                                </select>
                                                {/* Call Category pill — inline editable dropdown */}
                                                <QCCategoryPill
                                                  call={call}
                                                  categories={qcCategories}
                                                  onUpdate={(newCat) => {
                                                    setSellerCalls(prev => ({
                                                      ...prev,
                                                      [group.phone]: prev[group.phone].map(c => c.id === call.id ? { ...c, qc_category: newCat } : c)
                                                    }));
                                                  }}
                                                />
                                              </div>
                                            </div>
                                            {/* Right side — view action */}
                                            <Link
                                              to={`/calls/${call.id}`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                try {
                                                  sessionStorage.setItem('helix:lastOpenedCall', JSON.stringify({ callId: call.id, phone: group.phone, ts: Date.now() }));
                                                } catch {}
                                              }}
                                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-[#2eb4ac] hover:bg-[#2eb4ac]/5 rounded-lg transition-colors flex-shrink-0"
                                              data-testid={`open-call-${call.id}`}
                                            >
                                              <Eye className="w-4 h-4" />
                                              <span className="hidden md:inline">Open</span>
                                            </Link>
                                            {/* Re-analyze single call */}
                                            <button
                                              type="button"
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                const ok = window.confirm('Re-run AI analysis for this call? This will overwrite the existing analysis.');
                                                if (!ok) return;
                                                try {
                                                  toast.message('Re-analyzing…');
                                                  await axios.post(`${API}/calls/bulk-analyze?force=true`, [call.id]);
                                                  toast.success('Call re-analyzed');
                                                  // Refresh the seller's calls so the new analysis shows
                                                  const res = await axios.get(`${API}/calls/by-phone/${encodeURIComponent(group.phone)}?sort_order=desc`);
                                                  setSellerCalls(prev => ({ ...prev, [group.phone]: res.data.calls || [] }));
                                                  fetchData();
                                                } catch {
                                                  toast.error('Failed to re-analyze');
                                                }
                                              }}
                                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex-shrink-0"
                                              title="Re-run AI analysis"
                                              data-testid={`reanalyze-call-${call.id}`}
                                            >
                                              <RefreshCw className="w-4 h-4" />
                                              <span className="hidden md:inline">Re-analyze</span>
                                            </button>
                                          </div>

                                          {/* Summary text */}
                                          {call.summary && (
                                            <p className="text-sm text-slate-600 leading-relaxed mb-3 line-clamp-2">
                                              {call.summary}
                                            </p>
                                          )}

                                          {/* QC remark (editable) + agent (editable) row */}
                                          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3">
                                            <QCRemarkInline
                                              call={call}
                                              onUpdate={(newRem) => {
                                                setSellerCalls(prev => ({
                                                  ...prev,
                                                  [group.phone]: prev[group.phone].map(c => c.id === call.id ? { ...c, qc_remark: newRem } : c)
                                                }));
                                              }}
                                            />
                                            <AgentInline
                                              call={call}
                                              agents={agents}
                                              onUpdate={(newAg) => {
                                                setSellerCalls(prev => ({
                                                  ...prev,
                                                  [group.phone]: prev[group.phone].map(c => c.id === call.id ? { ...c, agent_id: newAg } : c)
                                                }));
                                              }}
                                            />
                                          </div>

                                          {/* Bottom row — audio player */}
                                          {call.audio_url && (
                                            <div className="pt-3 border-t border-slate-100">
                                              <MiniAudioPlayer audioUrl={`/api/audio/${encodeURIComponent(call.audio_url.split('/').pop())}`} />
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              <p className="text-center text-slate-500 py-6">No calls found</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer with Pagination */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
          <p>
            Showing <span className="font-semibold text-slate-700">{groupedData.length}</span> of <span className="font-semibold text-slate-700">{groupedTotal}</span> sellers
          </p>
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2eb4ac]/20 focus:border-[#2eb4ac] bg-white"
              data-testid="page-size-select"
            >
              {[10, 25, 50, 100].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          {/* Selection indicator — master checkbox at the table header handles select-all-on-page */}
          {selectedCalls.size > 0 && (
            <div className="flex items-center gap-3 pl-3 border-l border-slate-200">
              <span className="text-xs font-medium text-slate-700">
                {selectedCalls.size} selected
              </span>
              <button
                type="button"
                onClick={() => { setSelectedCalls(new Set()); setSelectedPhones(new Set()); }}
                className="text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors"
                data-testid="clear-selection-btn"
              >
                Clear
              </button>
            </div>
          )}
        </div>
        
        {/* Pagination Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          <div className="flex items-center gap-1">
            {[...Array(Math.min(5, totalPages))].map((_, idx) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = idx + 1;
              } else if (currentPage <= 3) {
                pageNum = idx + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + idx;
              } else {
                pageNum = currentPage - 2 + idx;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === pageNum 
                      ? 'bg-[#2eb4ac] text-white' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          
          <span className="text-sm text-slate-500 ml-2">
            Page {currentPage} of {totalPages}
          </span>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <ConfirmModal
          {...confirmModal}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* Bulk Analyze Progress Modal */}
      {isAnalyzing && analyzeProgress && (
        <AnalyzeProgressModal progress={analyzeProgress} />
      )}

      {/* Bulk Edit Modal — updates qc_category / qc_remark / agent for many calls at once */}
      {showBulkEditModal && (
        <BulkEditModal
          count={selectedCalls.size}
          categories={qcCategories}
          agents={agents}
          dataSources={dataSources}
          onCancel={() => setShowBulkEditModal(false)}
          onSubmit={async (payload) => {
            try {
              const res = await axios.post(`${API}/bulk-update-calls`, {
                call_ids: Array.from(selectedCalls),
                ...payload,
              });
              toast.success(res.data?.message || 'Updated');
              setShowBulkEditModal(false);
              setSelectedCalls(new Set());
              setSellerCalls({});
              fetchData();
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Failed to update');
            }
          }}
        />
      )}
    </div>
  );
};

export default CallLogs;

// Inline keyframes for table refetch indicator (kept module-scoped to keep CallLogs clean)
if (typeof document !== 'undefined' && !document.getElementById('helix-top-bar-keyframes')) {
  const style = document.createElement('style');
  style.id = 'helix-top-bar-keyframes';
  style.innerHTML = `@keyframes top-bar-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }`;
  document.head.appendChild(style);
}
